import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';

export type SyncStatus = 'offline' | 'saving' | 'saved' | 'syncing' | 'synced' | 'error';

interface SyncState {
  readonly online: boolean;
  readonly status: SyncStatus;
  readonly setStatus: (status: SyncStatus) => void;
}

const SyncStatusContext = createContext<SyncState | null>(null);

export function SyncStatusProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [status, setStatus] = useState<SyncStatus>(() => (navigator.onLine ? 'synced' : 'offline'));

  useEffect(() => {
    const handleOnline = (): void => {
      setOnline(true);
      setStatus('syncing');
    };
    const handleOffline = (): void => {
      setOnline(false);
      setStatus('offline');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const value = useMemo<SyncState>(() => ({ online, status, setStatus }), [online, status]);
  return <SyncStatusContext.Provider value={value}>{children}</SyncStatusContext.Provider>;
}

export function useSyncStatus(): SyncState {
  const context = useContext(SyncStatusContext);
  if (!context) throw new Error('useSyncStatus deve ser usado dentro de SyncStatusProvider.');
  return context;
}
